import { useEffect, useState } from "react";
import { Dialog, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactsheetViewer from "../factsheet/FactsheetViewer";
import {
    consumeFactsheetRequest,
    factsheetExists,
    factsheetUrlForCountryCode,
    iso2ToIso3,
} from "../../data/factsheets";

export default function CountryFactsheet({ country }) {
    const url = factsheetUrlForCountryCode(country?.code);
    const iso3 = country?.code ? iso2ToIso3[country.code.toUpperCase()] : null;
    const [available, setAvailable] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setOpen(false);
        setAvailable(false);
        if (!url) return;

        const controller = new AbortController();
        let active = true;
        factsheetExists(url, controller.signal)
            .then((exists) => active && setAvailable(exists))
            .catch(() => active && setAvailable(false));

        return () => {
            active = false;
            controller.abort();
        };
    }, [url]);

    // Arriving from a /factsheet/:iso3 deep link: FactsheetPage selects the country, sends
    // us to the normal indicators page and leaves the request behind, so the link ends up on
    // exactly the view the toolbar button produces. Consumed once, so closing it sticks.
    useEffect(() => {
        if (!available) return;
        if (consumeFactsheetRequest(iso3)) setOpen(true);
    }, [available, iso3]);

    if (!available) return null;

    const title = `${country.label} factsheet`;

    return (
        <>
            <Tooltip title={title}>
                <IconButton
                    aria-label={title}
                    onClick={() => setOpen(true)}
                    sx={{ mr: { xs: "3px", md: "6px" }, ml: "-6px", color: "primary.main" }}
                >
                    <DescriptionOutlinedIcon />
                </IconButton>
            </Tooltip>
            {/* The viewer carries its own toolbar and body, so it goes straight into the
                dialog: wrapping it in DialogTitle would nest the iframe inside a heading. */}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="lg"
                aria-label={title}
            >
                <FactsheetViewer
                    iso3={iso3}
                    url={url}
                    title={title}
                    height="80vh"
                    endActions={
                        <IconButton aria-label="Close" onClick={() => setOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    }
                />
            </Dialog>
        </>
    );
}
